import { t } from '@glory/i18n';
import { ChevronDown, Copy, MessageCircle, Phone } from 'lucide-react';
import { type ChangeEvent, useLayoutEffect, useRef, useState } from 'react';

import { Popover, PopoverAnchor, PopoverContent } from '../components/popover.tsx';
import { notify } from '../components/toaster.tsx';
import { cn } from '../lib/cn.ts';
import { matchesWords } from '../lib/text-match.ts';
import { type BoxProps, useBoxState, useDraft } from './draft.tsx';
import {
  Field,
  FieldAction,
  FieldActionLink,
  FieldBox,
  type FieldPart,
  inputClass,
  splitField,
} from './field.tsx';
import { CountryFlag, POPULAR_COUNTRIES } from './flags.tsx';
import { ListMessage, ListPanel, OptionRow } from './listbox.tsx';
import {
  type CountryCode,
  type CountryOption,
  DEFAULT_COUNTRY,
  detectCountry,
  formatPhoneInput,
  formatPhoneLive,
  parsePhoneInput,
  type PhoneProblem,
  phoneCountries,
  phoneLinks,
  type PhoneValue,
  toInternationalDigits,
} from './parsers/phone.ts';

const COUNTRIES = phoneCountries(POPULAR_COUNTRIES);

const PROBLEMS: Record<PhoneProblem, string> = {
  too_short: t('fields.phoneTooShort'),
  too_long: t('fields.phoneTooLong'),
  wrong_length: t('fields.phoneWrongLength'),
  invalid: t('fields.phoneInvalid'),
};

const digitsOf = (text: string) => text.replace(/\D/g, '');

export interface PhoneInputProps extends Omit<BoxProps, 'today'> {
  readonly value: PhoneValue | null;
  readonly onValueChange: (value: PhoneValue | null) => void;
  /** Страна для номера без кода; по умолчанию Россия. */
  readonly defaultCountry?: CountryCode;
  /** Кнопки «скопировать», «позвонить», «WhatsApp» у заполненного номера. */
  readonly actions?: boolean;
}

function usePhoneState(props: PhoneInputProps) {
  const initial = props.value === null ? null : detectCountry(props.value);
  const [country, setCountry] = useState<CountryCode>(initial ?? props.defaultCountry ?? DEFAULT_COUNTRY);
  const draft = useDraft<PhoneValue>({
    value: props.value,
    onValueChange: props.onValueChange,
    parse: (text) => parsePhoneInput(text, country).e164,
    format: formatPhoneInput,
    invalidMessage: (text) => PROBLEMS[parsePhoneInput(text, country).problem ?? 'invalid'],
  });
  return { draft, country, setCountry };
}

function PhoneBox({ state, props }: { state: ReturnType<typeof usePhoneState>; props: PhoneInputProps }) {
  const { draft, country, setCountry } = state;
  const {
    value,
    onValueChange: _change,
    defaultCountry: _default,
    actions = true,
    defaultOpen = false,
    size,
    variant,
    className,
    placeholder,
    ...box
  } = props;
  const { control, disabled, readOnly, invalid, locked } = useBoxState(box);
  const [open, setOpen] = useState(defaultOpen);
  const [query, setQuery] = useState('');
  const input = useRef<HTMLInputElement | null>(null);
  const caret = useRef<number | null>(null);

  // После маски вернуть курсор после той же цифры.
  useLayoutEffect(() => {
    if (caret.current !== null && input.current === document.activeElement) {
      input.current?.setSelectionRange(caret.current, caret.current);
    }
    caret.current = null;
  });

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    let text = e.target.value;
    let position = e.target.selectionStart ?? text.length;
    // Backspace по скобке или дефису маски стирает цифру перед ней, а не «ничего».
    const typing = (e.nativeEvent as InputEvent).inputType;
    if (typing === 'deleteContentBackward' && digitsOf(text) === digitsOf(draft.text)) {
      const before = text.slice(0, position);
      const index = before.search(/\d(?=\D*$)/);
      if (index >= 0) {
        text = text.slice(0, index) + text.slice(index + 1);
        position = index;
      }
    }
    const next = formatPhoneLive(text, position, country);
    if (next.country) setCountry(next.country);
    caret.current = next.caret;
    draft.setText(next.text);
  };

  const chooseCountry = (option: CountryOption) => {
    const current = toInternationalDigits(draft.text, country);
    const oldCode = COUNTRIES.find((c) => c.code === country)?.callingCode ?? '';
    const national = current.startsWith(`+${oldCode}`) ? current.slice(oldCode.length + 1) : '';
    setCountry(option.code);
    draft.setText(formatPhoneLive(`+${option.callingCode}${national}`, 999, option.code).text);
    setOpen(false);
  };

  const visible = COUNTRIES.filter((c) => matchesWords([c.name, c.code, `+${c.callingCode}`], query));
  const current = COUNTRIES.find((c) => c.code === country);
  const ready = value !== null && !draft.editing ? value : null;
  const links = ready === null ? null : phoneLinks(ready);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next && !locked);
        if (!next) setQuery('');
      }}
    >
      <PopoverAnchor asChild>
        <FieldBox
          size={size}
          variant={variant}
          disabled={disabled}
          readOnly={readOnly}
          invalid={invalid || draft.error !== null}
          className={cn('pl-1.5', className)}
          leading={
            <button
              type="button"
              aria-label={t('fields.phoneCountry', { country: current?.name ?? country })}
              aria-haspopup="listbox"
              aria-expanded={open}
              disabled={locked}
              className="flex h-6 items-center gap-1 rounded-sm px-1 hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none disabled:pointer-events-none"
              onClick={() => {
                setOpen(!open);
              }}
            >
              <CountryFlag code={country} />
              {locked ? null : <ChevronDown aria-hidden="true" className="size-3.5!" />}
            </button>
          }
          trailing={
            ready !== null && links && actions && !disabled ? (
              <>
                <FieldAction
                  label={t('fields.phoneCopy')}
                  onClick={() => {
                    void navigator.clipboard.writeText(formatPhoneInput(ready)).then(() => {
                      notify(t('fields.phoneCopied'));
                    });
                  }}
                >
                  <Copy />
                </FieldAction>
                <FieldActionLink label={t('fields.phoneCall')} href={links.call}>
                  <Phone />
                </FieldActionLink>
                <FieldActionLink label={t('fields.phoneWhatsApp')} href={links.whatsapp} external>
                  <MessageCircle />
                </FieldActionLink>
              </>
            ) : undefined
          }
        >
          <input
            ref={input}
            {...control}
            {...draft.inputProps}
            onChange={onChange}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            aria-label={props['aria-label']}
            aria-invalid={draft.error !== null || control['aria-invalid']}
            disabled={disabled}
            readOnly={readOnly}
            placeholder={placeholder ?? t('fields.phonePlaceholder')}
            className={cn(inputClass, 'tabular-nums')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && draft.editing) {
                e.preventDefault();
                draft.commit();
              } else if (e.key === 'Escape' && draft.editing) {
                e.stopPropagation();
                draft.revert();
              }
            }}
          />
        </FieldBox>
      </PopoverAnchor>
      <PopoverContent
        className="w-72 p-0"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          input.current?.focus();
        }}
      >
        <ListPanel
          items={visible}
          getKey={(c) => c.code}
          isSelected={(c) => c.code === country}
          initialKey={country}
          label={t('fields.phoneCountries')}
          search={{ value: query, onChange: setQuery }}
          message={visible.length === 0 ? <ListMessage>{t('fields.selectEmpty')}</ListMessage> : undefined}
          onSelect={chooseCountry}
          renderItem={(c, row) => (
            <OptionRow
              label={c.name}
              icon={<CountryFlag code={c.code} />}
              aside={`+${c.callingCode}`}
              active={row.active}
              selected={row.selected}
            />
          )}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Телефон: страна с флагом и поиском (по умолчанию Россия), маска `+7 (999) 123-45-67` при вводе,
 * вставка в любом виде (`8 999…`, `+7…`, `00…`), хранение в E.164; скопировать, позвонить, WhatsApp.
 */
export function PhoneInput(props: PhoneInputProps) {
  const state = usePhoneState(props);
  return <PhoneBox state={state} props={props} />;
}

export interface PhoneFieldProps extends FieldPart, Omit<PhoneInputProps, keyof FieldPart> {}

export function PhoneField(props: PhoneFieldProps) {
  const [field, input] = splitField(props);
  const state = usePhoneState(input);
  return (
    <Field {...field} error={field.error ?? state.draft.error}>
      <PhoneBox state={state} props={input} />
    </Field>
  );
}
